/** Long-wavelength value noise, in integers.
 *
 * This exists because per-cell noise cannot bend a path. A Dijkstra over a flat
 * cost field returns the L-shaped Manhattan route between two cells; adding a
 * different random number to every cell independently does not change that, it
 * only re-rolls *which* equal-length staircase wins. Every candidate is still a
 * chain of axis-aligned runs joined at right angles, which is what makes a
 * generated road network read as a circuit board.
 *
 * A field that is smooth over eight or ten cells does change it. Two routes of
 * the same length now differ in cost by the integral of the field along them,
 * so the cheapest one bows around whatever the field made expensive, and the
 * bow is as wide as the lattice - tens of cells, not one. Same idea for a
 * river: the lateral choice at each step has to agree with the lateral choice
 * ten steps ago or the channel jitters instead of meandering.
 *
 * Integer fixed point throughout, `NOISE_ONE` to the unit, so a map is the same
 * map on every machine. This is not `packages/core`, but a generator that is
 * only reproducible on one CPU is not reproducible.
 */

/** Fixed-point unit. Noise samples come back in `0 .. NOISE_ONE - 1`. */
export const NOISE_ONE = 1024;

/** Hash of a lattice corner. Deterministic, and mixes both axes and the salt. */
function latticeHash(gx: number, gy: number, salt: number): number {
  let h = (Math.imul(gx | 0, 0x27d4eb2d) ^ Math.imul(gy | 0, 0x165667b1)
    ^ Math.imul(salt | 0, 0x9e3779b9)) >>> 0;
  h ^= h >>> 15; h = Math.imul(h, 0x2c1b3c6d) >>> 0;
  h ^= h >>> 12; h = Math.imul(h, 0x297a2d39) >>> 0;
  h ^= h >>> 15;
  return h >>> 0;
}

/** `3t^2 - 2t^3` on `0..NOISE_ONE`; the C1 blend that keeps lattice lines invisible. */
function smoothstep(t: number): number {
  const t2 = Math.trunc((t * t) / NOISE_ONE);
  const t3 = Math.trunc((t2 * t) / NOISE_ONE);
  return 3 * t2 - 2 * t3;
}

/**
 * One octave of value noise on a `period`-cell lattice, in `0..NOISE_ONE - 1`.
 *
 * `period` is the wavelength in map cells and is the whole point: it has to be
 * large enough that a road spends several steps inside one lattice square, or
 * the field is per-cell noise again under another name.
 */
export function valueNoise(x: number, y: number, period: number, salt: number): number {
  const p = period < 1 ? 1 : period;
  const gx = Math.floor(x / p), gy = Math.floor(y / p);
  const fx = Math.trunc(((x - gx * p) * NOISE_ONE) / p);
  const fy = Math.trunc(((y - gy * p) * NOISE_ONE) / p);
  const sx = smoothstep(fx), sy = smoothstep(fy);
  const c00 = latticeHash(gx, gy, salt) % NOISE_ONE;
  const c10 = latticeHash(gx + 1, gy, salt) % NOISE_ONE;
  const c01 = latticeHash(gx, gy + 1, salt) % NOISE_ONE;
  const c11 = latticeHash(gx + 1, gy + 1, salt) % NOISE_ONE;
  const top = c00 + Math.trunc(((c10 - c00) * sx) / NOISE_ONE);
  const bot = c01 + Math.trunc(((c11 - c01) * sx) / NOISE_ONE);
  return top + Math.trunc(((bot - top) * sy) / NOISE_ONE);
}

/**
 * A relief field: two octaves, in `0..NOISE_ONE - 1`.
 *
 * The long octave draws hills the size of `LONG_PERIOD`; the short one gives
 * their edges a ragged outline so a road that skirts one does not trace a
 * circle. The short octave is a minority share on purpose - at parity the sum
 * is white noise over the distance a road covers and the hills stop existing.
 */
export function relief(x: number, y: number, salt: number): number {
  const lo = valueNoise(x, y, LONG_PERIOD, salt);
  const hi = valueNoise(x, y, SHORT_PERIOD, salt ^ 0x5bf03635);
  return Math.trunc((lo * 3 + hi) / 4);
}

/**
 * Wavelengths in map cells.
 *
 * `LONG_PERIOD` is the width of a hill, and it has to be a good fraction of the
 * distance between two towns or a route just averages over several hills and
 * runs straight anyway. Measured on a 60-cell route across an empty 72-cell
 * grid: a *smooth* field, at any amplitude up to twenty times the base step,
 * moved the cheapest path off the straight line by exactly zero cells. The
 * reason is that a detour costs two extra cells outright while the saving from
 * a smoother lane is an average along the route, and an average over tens of
 * cells is small. Only a field with somewhere the road genuinely must not go -
 * a *threshold*, not a gradient - bends it. See `ROAD_HIGH_GROUND`.
 */
export const LONG_PERIOD = 12;
export const SHORT_PERIOD = 4;
